import { useXHR, type XHRInstance } from "@sembark-travel/xhr"
import useSWR from "swr"

export interface IPermission {
	id: number
	name: string
	is_revokable: boolean
	description?: string
	title?: string
}

export interface IRole {
	id: number
	name: string
	internal_name: string
	created_at: string
	updated_at: string
	permissions?: IPermission[]
	revokable_permissions?: string[]
}

export function getUniqueRevocablePermissions(
	roles: IRole[] = [],
	revoked_permissions?: string[]
): IPermission[] {
	const revokedSet = new Set(revoked_permissions ?? [])
	const seen = new Map<number, IPermission>()

	for (const role of roles) {
		for (const p of role.permissions || []) {
			if (seen.has(p.id)) continue

			const isRevokable = p.is_revokable

			if (isRevokable) {
				seen.set(p.id, { ...p, is_revokable: !revokedSet.has(p.name) })
			}
		}
	}

	return Array.from(seen.values()).sort((a, b) => a.id - b.id)
}

export function RolesXHR(xhr: XHRInstance) {
	return {
		async getRole(id: number | string): Promise<IRole> {
			return xhr.get(`/roles/${id}`).then(({ data }) => data.data)
		},
	}
}

export function useRole(roleId: string | number) {
	const xhr = useXHR()
	const {
		data,
		error,
		isValidating,
		mutate: revalidate,
	} = useSWR(`/api/roles/${roleId}`, () => RolesXHR(xhr).getRole(roleId))
	const isFetching = !data && !error
	const isRefreshing = isValidating
	return {
		role: data,
		isFetching,
		isRefreshing,
		fetchRole: revalidate,
	}
}
